import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

interface LoginRow {
  email: string;
  logged_in_at: string;
}

interface UserSummary {
  email: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
  logins_7d: number;
  logins_30d: number;
  logins_90d: number;
  logins_total: number;
}

type Tab = 'log' | 'summary';
type Period = 'logins_7d' | 'logins_30d' | 'logins_90d' | 'logins_total';

const PERIODS: { key: Period; label: string }[] = [
  { key: 'logins_7d', label: 'Last 7 days' },
  { key: 'logins_30d', label: 'Last 1 month' },
  { key: 'logins_90d', label: 'Last 3 months' },
  { key: 'logins_total', label: 'All time' },
];

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

async function getJson<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetchApi(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

export const UsageStatsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('log');
  const [days, setDays] = useState(30);
  const [logs, setLogs] = useState<LoginRow[]>([]);
  const [summary, setSummary] = useState<UserSummary[]>([]);
  const [period, setPeriod] = useState<Period>('logins_30d');
  const [zeroOnly, setZeroOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const load =
      tab === 'log'
        ? getJson<LoginRow[]>(`/admin/usage-stats/logins?days=${days}&limit=5000`).then(setLogs)
        : getJson<UserSummary[]>('/admin/usage-stats/summary').then(setSummary);
    load.catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [tab, days]);

  const q = search.trim().toLowerCase();
  const shownLogs = useMemo(
    () => logs.filter((l) => !q || l.email.toLowerCase().includes(q)),
    [logs, q],
  );
  const shownUsers = useMemo(
    () =>
      summary
        .filter((u) => (!q || u.email.toLowerCase().includes(q)) && (!zeroOnly || u[period] === 0))
        .sort((a, b) => b[period] - a[period] || a.email.localeCompare(b.email)),
    [summary, q, zeroOnly, period],
  );
  const zeroCount = summary.filter((u) => u[period] === 0).length;

  const tabClass = (t: Tab) =>
    `px-4 py-2 text-sm font-medium border-b-2 ${
      tab === t
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
    }`;
  const inputClass =
    'px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white';
  const th = 'px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300';
  const td = 'px-3 py-2 text-slate-900 dark:text-slate-100';

  return (
    <div className="p-6 flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 size={28} className="text-slate-700 dark:text-slate-300" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Usage Stats</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Who logged in and how often. Times show in your local time zone.
            </p>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 mb-4">
          <button className={tabClass('log')} onClick={() => setTab('log')}>Login Log</button>
          <button className={tabClass('summary')} onClick={() => setTab('summary')}>Team Summary</button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            className={inputClass}
            placeholder="Filter by email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {tab === 'log' ? (
            <select className={inputClass} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 1 month</option>
              <option value={90}>Last 3 months</option>
              <option value={365}>Last 12 months</option>
            </select>
          ) : (
            <>
              <select className={inputClass} value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                {PERIODS.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={zeroOnly} onChange={(e) => setZeroOnly(e.target.checked)} />
                Only users with 0 logins ({zeroCount})
              </label>
            </>
          )}
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {loading && <p className="text-sm text-slate-500 mb-3">Loading…</p>}

        {tab === 'log' ? (
          <>
            <p className="text-xs text-slate-500 mb-2">{shownLogs.length} logins</p>
            <table className="w-full text-sm border border-slate-200 dark:border-slate-700">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr><th className={th}>Email</th><th className={th}>Date / Time</th></tr>
              </thead>
              <tbody>
                {shownLogs.map((l, i) => (
                  <tr key={i} className="border-t border-slate-200 dark:border-slate-700">
                    <td className={td}>{l.email}</td>
                    <td className={td}>{fmt(l.logged_in_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-2">{shownUsers.length} users</p>
            <table className="w-full text-sm border border-slate-200 dark:border-slate-700">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th className={th}>Email</th>
                  <th className={th}>Last login</th>
                  {PERIODS.map((p) => (
                    <th key={p.key} className={`${th} text-right ${p.key === period ? 'text-blue-600' : ''}`}>
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownUsers.map((u) => (
                  <tr key={u.email} className="border-t border-slate-200 dark:border-slate-700">
                    <td className={td}>
                      {u.email}
                      {!u.is_active && <span className="ml-2 text-xs text-slate-500">(inactive)</span>}
                    </td>
                    <td className={td}>{u.last_login ? fmt(u.last_login) : 'Never'}</td>
                    {PERIODS.map((p) => (
                      <td key={p.key} className={`${td} text-right ${u[p.key] === 0 ? 'text-red-600' : ''}`}>
                        {u[p.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};
